import { Phone, Building, User, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import InlineCallStatus from '@/components/dialer/InlineCallStatus';
import { dialIntent } from '@/services/dialer/dialRules';

/**
 * GridView, troisième vue du dialer, demandée par la doléance #4.
 *
 * « NOUVELLE VUE ANIMÉE : 3e mode d'affichage en grille, contacts en file
 *   d'attente et terminés affichés différemment »
 * « Quand un numéro sonne : animation laser tournante (contour jaune) »
 * « Les contacts terminés apparaissent distinctement en bas »
 *
 * Le classement est DÉRIVÉ, exactement comme celui du kanban réparé en #4b :
 * dialIntent() pour l'intention d'appel, prospectCallMap pour « en cours ».
 * C'est la règle posée par l'ADR-032, et le contraire de ce que faisait
 * l'ancien KanbanView, qui inventait son propre vocabulaire de statuts.
 *
 * Une seule source pour les deux vues : si le classement change un jour, il
 * change au même endroit pour les deux.
 */

type CallShape = Parameters<typeof InlineCallStatus>[0]['call'];

interface ProspectShape {
  id: string;
  prospect_name?: string | null;
  prospect_company?: string | null;
  prospect_phone?: string | null;
}

interface GridViewProps {
  prospects: ProspectShape[];
  onProspectClick: (prospect: ProspectShape) => void;
  callMap?: Record<string, NonNullable<CallShape>>;
  latestCallByPhone?: Record<string, unknown>;
  currentTime?: Date | number;
}

/** Statuts d'un appel qui n'a pas encore joint : c'est « ça sonne ». */
const STATUTS_SONNERIE = new Set([
  'dialing', 'ringing', 'initiated', 'queued', 'ai_analyzing', 'human_detected',
]);

export default function GridView({
  prospects,
  onProspectClick,
  callMap = {},
  latestCallByPhone = {},
  currentTime,
}: GridViewProps) {
  const { t } = useTranslation('dialer');
  // Deux groupes seulement : ce qui reste à traiter (en haut), ce qui est
  // conclu (en bas). C'est la demande, pas quatre colonnes de plus.
  const enCours = [];
  const aTraiter = [];
  const termines = [];

  for (const p of prospects) {
    const phone = p.prospect_phone || '';
    const actif = phone ? callMap[phone] : null;
    if (actif) { enCours.push(p); continue; }

    const intent = dialIntent(p, latestCallByPhone[phone] || null);
    if (intent === 'fresh' || intent === 'no_answer') aTraiter.push(p);
    else termines.push(p);
  }

  const Carte = ({ prospect, fini }: { prospect: ProspectShape; fini?: boolean }) => {
    const phone = prospect.prospect_phone || '';
    const actif = phone ? callMap[phone] : null;
    const sonne = !!actif && STATUTS_SONNERIE.has(actif.call_status ?? '');

    return (
      <div
        data-testid={fini ? 'grid-carte-terminee' : 'grid-carte'}
        data-sonne={sonne ? 'oui' : 'non'}
        onClick={() => onProspectClick(prospect)}
        className={`vf-grid-carte ${sonne ? 'vf-sonne' : ''}`}
        style={{
          background: 'var(--vf-bg)',
          border: '1px solid var(--vf-border)',
          borderRadius: 12,
          padding: 12,
          cursor: 'pointer',
          // Les terminés restent lisibles mais visiblement en retrait :
          // ils ne sont plus la cible de travail de l'agent.
          opacity: fini ? 0.62 : 1,
        }}
      >
        <div className="flex items-center gap-2 mb-2">
          {fini
            ? <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--vf-green, #10b981)' }} />
            : <User className="w-3.5 h-3.5 text-muted-foreground/60 flex-shrink-0" />}
          <p className="font-medium text-sm text-foreground truncate">{prospect.prospect_name}</p>
        </div>

        {prospect.prospect_company && (
          <div className="flex items-center gap-2 mb-1.5">
            <Building className="w-3 h-3 text-muted-foreground/60 flex-shrink-0" />
            <p className="text-xs text-muted-foreground truncate">{prospect.prospect_company}</p>
          </div>
        )}

        <div className="flex items-center gap-2">
          <Phone className="w-3 h-3 text-muted-foreground/60 flex-shrink-0" />
          <p className="text-xs text-muted-foreground font-mono">{prospect.prospect_phone}</p>
        </div>

        {actif && (
          <div className="mt-2">
            <InlineCallStatus call={actif} currentTime={currentTime} />
          </div>
        )}
      </div>
    );
  };

  const Section = ({ titre, compte, enfants, testid }: {
    titre: string;
    compte: number;
    enfants: React.ReactNode;
    testid: string;
  }) => (
    <section data-testid={testid} style={{ marginBottom: 22 }}>
      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-sm font-medium" style={{ color: 'var(--vf-text-2)' }}>{titre}</h3>
        <span
          className="text-[11px] font-semibold"
          style={{
            background: 'var(--vf-surface-2)', border: '1px solid var(--vf-border)',
            borderRadius: 99, padding: '1px 7px', color: 'var(--vf-text-2)',
          }}
        >
          {compte}
        </span>
      </div>
      {compte === 0
        ? <p className="text-xs text-muted-foreground/70">{t('dialer:kanbanView.noContacts')}</p>
        : <div className="vf-grid-cartes">{enfants}</div>}
    </section>
  );

  return (
    <div data-testid="vue-grille">
      {enCours.length > 0 && (
        <Section
          titre={t('dialer:gridView.inProgress')} compte={enCours.length} testid="grid-section-encours"
          enfants={enCours.map((p) => <Carte key={p.id} prospect={p} fini={false} />)}
        />
      )}

      <Section
        titre={t('dialer:gridView.queue')} compte={aTraiter.length} testid="grid-section-attente"
        enfants={aTraiter.map((p) => <Carte key={p.id} prospect={p} fini={false} />)}
      />

      {/* #4, « les contacts terminés apparaissent distinctement en bas » :
          dernière section, séparateur franc, cartes en retrait. */}
      <div style={{ borderTop: '1px solid var(--vf-border)', paddingTop: 18 }}>
        <Section
          titre={t('dialer:gridView.completed')} compte={termines.length} testid="grid-section-termines"
          enfants={termines.map((p) => <Carte key={p.id} prospect={p} fini />)}
        />
      </div>
    </div>
  );
}
